import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RefreshCcw, Share2, Calendar } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { TopBar, Footer } from "@/components/Brand";
import StaggerText from "@/components/StaggerText";
import AudioPlayer from "@/components/AudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Result() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [shared, setShared] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("clarity_result");
    if (!raw) {
      navigate("/");
      return;
    }
    const parsed = JSON.parse(raw);
    setData(parsed);
    axios.get(`${API}/quiz/videos`).then((r) => {
      const key = parsed?.archetype_key;
      if (key && r.data?.[key]) setVideoUrl(r.data[key]);
    }).catch(() => {});
  }, [navigate]);

  if (!data) return null;
  const a = data.archetype;
  const audioSrc = `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`;

  const share = async () => {
    const url = "https://quiz.chelsealitsey.com";
    const text = `I just discovered my Energy Archetype: ${a.name}. Take Chelsea Litsey's 2-minute quiz:`;
    if (navigator.share) {
      try { await navigator.share({ title: "Energy Archetype Quiz", text, url }); setShared(true); } catch {}
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setShared(true);
      setTimeout(() => setShared(false), 2400);
    }
  };

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      <svg className="pointer-events-none absolute -top-40 -right-40 w-[900px] opacity-[0.07]" viewBox="0 0 600 600" fill="none">
        <circle cx="300" cy="300" r="260" stroke="#0E0E0E" strokeWidth="1" />
        <circle cx="300" cy="300" r="200" stroke="#0E0E0E" strokeWidth="1" />
        <circle cx="300" cy="300" r="140" stroke="#0E0E0E" strokeWidth="1" />
      </svg>

      <TopBar />

      <main className="px-6 sm:px-10 lg:px-16 pt-6 pb-20 relative">
        <div className="max-w-7xl mx-auto">
          {/* Hero reveal */}
          <section className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end pb-16 border-b border-cream-300/70">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-700 mb-6"
              >
                <span className="inline-block h-px w-10 align-middle bg-sage-700/60 mr-3" />
                Your Energy Archetype
              </motion.p>

              <StaggerText
                as="h1"
                testId="result-archetype-name"
                text={a.name}
                className="font-display text-[3.2rem] sm:text-7xl lg:text-[6rem] tracking-tight text-ink leading-[0.95]"
              />

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="mt-8 font-display italic-display text-2xl sm:text-3xl text-sage-900 max-w-xl leading-snug"
              >
                {a.tagline}
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-[4/5] rounded-sm overflow-hidden bg-ink">
                <img src={ASSETS.results_color_smile} alt="Chelsea Litsey" className="absolute inset-0 h-full w-full object-cover object-top" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent p-5 text-cream-100">
                  <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-sage-300 mb-1">A note from</p>
                  <p className="font-display text-xl">Chelsea Litsey</p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Story + drains */}
          <section className="grid lg:grid-cols-12 gap-10 lg:gap-16 py-16 border-b border-cream-300/70">
            <div className="lg:col-span-7 space-y-5">
              {a.story.map((s, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="font-sans text-lg sm:text-xl text-ink/80 leading-relaxed"
                >
                  {s}
                </motion.p>
              ))}
            </div>
            <div className="lg:col-span-5">
              <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-sage-700 mb-4">How this drains you</p>
              <ul className="space-y-3">
                {a.drains.map((d) => (
                  <li key={d} className="flex items-start gap-3 font-sans text-base text-ink/80">
                    <span className="mt-2 inline-block h-px w-6 bg-sage-700/70" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Audio */}
          <section className="py-16">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-5">
                <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-700 mb-4">
                  <span className="inline-block h-px w-10 align-middle bg-sage-700/60 mr-3" />
                  Free · Yours instantly
                </p>
                <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight tracking-tight">
                  Press play. Let your nervous system exhale.
                </h2>
                <p className="mt-5 font-sans text-base text-ink/70 leading-relaxed max-w-md">
                  This Mind Magic Audio was crafted to calm the exact loop “{a.name}” runs on. Listen daily for 7 days — most women feel a shift by day 3.
                </p>
              </div>
              <div className="lg:col-span-7">
                <AudioPlayer src={audioSrc} title={a.meditation_title} subtitle={`A Mind Magic Audio for ${a.name}`} />
              </div>
            </div>
          </section>

          {/* Welcome video from Chelsea */}
          <section className="py-16 border-t border-cream-300/70">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-700 mb-4">A welcome from Chelsea</p>
                <h2 className="font-display text-3xl sm:text-4xl text-ink leading-tight tracking-tight">
                  What your archetype means.
                </h2>
                <p className="mt-4 font-sans text-base text-ink/70 leading-relaxed">
                  A 90-second message on the smallest shift that makes the meditation land 10x deeper.
                </p>
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative rounded-sm overflow-hidden bg-ink border border-sage-700/30 flex items-center justify-center" style={{ minHeight: "320px" }} data-testid="welcome-video-frame">
                  {videoUrl ? (
                    /\.(mp4|webm|mov|m4v)(\?|$)/i.test(videoUrl) ? (
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        className="max-w-full w-auto h-auto bg-ink"
                        style={{ maxHeight: "min(78vh, 720px)", display: "block" }}
                        data-testid="welcome-video-native"
                      >
                        <source src={videoUrl} type="video/mp4" />
                        <source src={videoUrl} type="video/quicktime" />
                        Your browser does not support HTML5 video.
                      </video>
                    ) : (
                      <div className="relative w-full aspect-video">
                        <iframe
                          title="Welcome video from Chelsea"
                          src={videoUrl}
                          className="absolute inset-0 h-full w-full"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                          allowFullScreen
                          data-testid="welcome-video-iframe"
                        />
                      </div>
                    )
                  ) : (
                    <div className="w-full aspect-video flex items-center justify-center text-cream-100/70 font-display italic-display text-xl px-6 text-center">
                      A short welcome from Chelsea is on the way.
                    </div>
                  )}
                  <div className="absolute inset-0 ring-1 ring-inset ring-cream-100/10 pointer-events-none" />
                </div>
              </div>
            </div>
          </section>

          {/* Upgrade CTA */}
          <section className="py-16 border-t border-cream-300/70">
            <div className="bg-ink text-cream-100 rounded-sm p-10 sm:p-14 relative overflow-hidden">
              <div className="grid md:grid-cols-2 gap-10 relative z-10 items-center">
                <div>
                  <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-300 mb-4">Go deeper</p>
                  <h3 className="font-display text-3xl sm:text-5xl tracking-tight">
                    {a.cta_hook}
                  </h3>
                  <p className="mt-4 font-sans text-cream-100/70 max-w-md">
                    Imagine a Mind Magic Audio crafted only for your nervous system, your patterns, your timeline. That’s a Custom Mind Magic Audio.
                  </p>
                </div>
                <div className="md:text-right space-y-4">
                  <a
                    href="https://chelsealitsey.com/programs"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-3 bg-sage-500 text-ink px-7 py-4 rounded-sm font-sans text-sm tracking-[0.18em] uppercase hover:bg-cream-100 transition-colors duration-500"
                    data-testid="result-upgrade-cta"
                  >
                    {a.upgrade_cta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <div>
                    <a
                      href="https://chelsealitsey.com"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-cream-100/70 hover:text-sage-300 link-reveal"
                      data-testid="result-book-call"
                    >
                      <Calendar className="h-3.5 w-3.5" /> Or book a free clarity call
                    </a>
                  </div>
                </div>
              </div>
              <svg className="absolute -right-24 -bottom-28 w-[600px] opacity-10" viewBox="0 0 400 400" fill="none">
                <circle cx="200" cy="200" r="180" stroke="#8EA78F" strokeWidth="1" />
                <circle cx="200" cy="200" r="140" stroke="#8EA78F" strokeWidth="1" />
                <circle cx="200" cy="200" r="100" stroke="#8EA78F" strokeWidth="1" />
              </svg>
            </div>
          </section>

          {/* Secondary actions */}
          <section className="py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-cream-300/70">
            <button
              type="button"
              onClick={() => { sessionStorage.removeItem("clarity_result"); navigate("/"); }}
              className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-ink/60 hover:text-sage-700 link-reveal"
              data-testid="result-retake"
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Retake the quiz
            </button>
            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-ink/60 hover:text-sage-700 link-reveal"
              data-testid="result-share"
            >
              <Share2 className="h-3.5 w-3.5" /> {shared ? "Link copied" : "Share with a friend"}
            </button>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
